import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import { Botao, EmptyState, FormSection, Modal, PageHeader } from '../../../ui';
import StepperVistoria from '../../../components/vistoria/StepperVistoria';
import CanvasAssinaturaVistoria, {
  ApiCanvasAssinatura,
} from '../../../components/vistoria/CanvasAssinaturaVistoria';

import { RootState } from '../../../store/store';
import { setSignatureBase64 } from '../../../store/vistoriaSlice';
import { uploadVistoriaSignature } from '../../../api/vistoria/vistoriaApi';
import {
  VISTORIA,
  VISTORIA_ASSINATURA_PREVIA,
  VISTORIA_CHECKLIST_ACESSORIOS,
  VISTORIA_LAUDO,
} from '../routes';

/**
 * `/vistoria/assinatura` — **etapa 3 do fluxo de vistoria** (reescrita na at-64,
 * frente D/D2).
 *
 * 🔴 **A rota mudou de nome.** Era `/assinatura-termo`, que descrevia outra
 * coisa: isto é a assinatura do associado no **laudo de vistoria**, não em termo
 * nenhum. O nome antigo obrigava `rotasComShell.ts` e `routes.test.ts` a manter
 * sentinelas explicando que `/assinatura` (a assinatura do CONTRATO, outro fluxo)
 * não a capturava por prefixo.
 *
 * ## O que mudou de comportamento
 *
 *   1. 🐛 **O "(stub)" saiu.** A tela mostrava **"Observação (stub):"** com a
 *      observação do checklist — texto de depuração que o associado lia na hora de
 *      assinar. O `state` que o trazia deixou de ter leitor; o que a tela usa
 *      (`vistoriaId`, nome do associado) vem do Redux.
 *   2. **Sem vistoria em andamento, a tela DIZ isso antes** — e não no clique de
 *      salvar, com um `toast.error` ("Vistoria não iniciada (sem ID)"). Acontece
 *      por link direto, aba nova ou F5 depois de um logout.
 *   3. **O campo de assinatura virou um `Modal` de verdade** (Esc, clique fora,
 *      foco preso), no mesmo envelope que a at-60 deu ao contrato: um botão abre o
 *      diálogo, e Assinar/Limpar ficam no rodapé dele.
 *   4. **"Assinar" vazio avisa DENTRO do diálogo**, com `role="alert"` — era um
 *      `toast.warn` que aparecia atrás do próprio campo.
 *   5. **Bolinhas dinâmicas** (`StepperVistoria` "Etapa 3 de 4") e cabeçalho com
 *      "Voltar". A tela tinha só um botão "Home" solto.
 *
 * ⚠️ **O que NÃO mudou:** a **resolução do canvas** (100% × 180px × DPR — é dela
 * que sai o PNG que vai ao PDF do laudo e ao SGA), o `POST /vistorias/:id/signature`,
 * o `setSignatureBase64` no Redux e a navegação ao laudo nos dois modos (fluxo e
 * `origemEdicao`).
 */
const VistoriaAssinaturaPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const voltar = useVoltar(VISTORIA_CHECKLIST_ACESSORIOS());

  const canvas = React.useRef<ApiCanvasAssinatura>(null);

  const associadoNome = useSelector((state: RootState) => state.associado.nomeCompleto);
  const vistoriaId = useSelector((state: RootState) => state.vistoria.vistoriaId);

  const [modalAberto, setModalAberto] = React.useState(false);
  const [salvando, setSalvando] = React.useState(false);
  /** "Assine antes de salvar" — dentro do diálogo, onde o campo está. */
  const [erroAssinatura, setErroAssinatura] = React.useState<string | null>(null);

  const salvar = async () => {
    if (!vistoriaId) return;

    if (!canvas.current || canvas.current.vazio()) {
      setErroAssinatura('Faça a assinatura antes de salvar.');
      return;
    }

    const base64 = canvas.current.paraDataUrl();
    if (!base64) {
      setErroAssinatura('Não foi possível capturar a assinatura. Tente novamente.');
      return;
    }

    setErroAssinatura(null);
    setSalvando(true);
    try {
      await uploadVistoriaSignature({ vistoriaId, signatureBase64: base64 });

      dispatch(setSignatureBase64(base64));

      toast.success('Assinatura salva com sucesso.');
      navigate(VISTORIA_LAUDO());
    } catch {
      toast.error('Falha ao salvar assinatura.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader sobretitulo="Vistoria" titulo="Assinatura do associado" aoVoltar={voltar} />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              <StepperVistoria etapa={3} />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              {!vistoriaId ? (
                <EmptyState
                  titulo="Nenhuma vistoria em andamento"
                  descricao="A assinatura é a terceira etapa de uma vistoria. Vá ao painel, escolha o veículo e inicie (ou continue) a vistoria por lá."
                  icone="alerta"
                  acao={
                    <Botao variante="primario" onClick={() => navigate(VISTORIA())}>
                      Ir para o painel
                    </Botao>
                  }
                />
              ) : (
                <FormSection titulo="Assinatura do associado" icone="pessoa">
                  <div className="tw-flex tw-flex-col tw-gap-3">
                    <p className="tw-m-0 tw-text-sm tw-text-texto-corpo">
                      O associado <strong>{associadoNome || '—'}</strong> assina na tela do
                      aparelho para o laudo ser gerado.
                    </p>

                    <Botao
                      variante="fantasma"
                      onClick={() => navigate(VISTORIA_ASSINATURA_PREVIA())}
                    >
                      Visualizar laudo antes de assinar
                    </Botao>

                    <Botao
                      variante="secundario"
                      larguraTotal
                      onClick={() => {
                        setErroAssinatura(null);
                        setModalAberto(true);
                      }}
                    >
                      Abrir campo de assinatura
                    </Botao>
                  </div>
                </FormSection>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        aberto={modalAberto}
        titulo="Assinatura do associado"
        descricao="Assine dentro da moldura."
        ocupado={salvando}
        aoFechar={() => setModalAberto(false)}
        rodape={
          <>
            <Botao
              variante="primario"
              carregando={salvando}
              disabled={salvando}
              onClick={() => void salvar()}
            >
              {salvando ? 'Salvando...' : 'Salvar assinatura'}
            </Botao>
            <Botao
              variante="secundario"
              disabled={salvando}
              onClick={() => {
                canvas.current?.limpar();
                setErroAssinatura(null);
              }}
            >
              Limpar
            </Botao>
          </>
        }
      >
        <div className="tw-flex tw-flex-col tw-gap-2">
          <CanvasAssinaturaVistoria ref={canvas} />

          {erroAssinatura && (
            <p
              role="alert"
              className="tw-m-0 tw-text-xs tw-font-semibold tw-text-danger"
            >
              {erroAssinatura}
            </p>
          )}
        </div>
      </Modal>
    </ShellSaac>
  );
};

export default VistoriaAssinaturaPage;
